import { GLOBAL_API_LIMIT } from "@/configs/api";
import { type DataTableOptions } from "@/configs/datatable";
import { type QueryFilter } from "@/utils/filter";

export interface QueryPaginationFilters {
  dataTableOptions: DataTableOptions;
  customFilters?: QueryFilter[];
  customOrFilters?: string;
}

export const mergePageParam = <T extends QueryPaginationFilters>(
  queryKeyPaginationFilters: T,
  pageParam?: number,
): T => {
  return {
    ...queryKeyPaginationFilters,
    dataTableOptions: {
      ...queryKeyPaginationFilters.dataTableOptions,
      page: pageParam ?? queryKeyPaginationFilters.dataTableOptions.page,
    },
  };
};

export const getPagination = (page = 0, size = 10) => {
  const _page = page - 1;
  // ! if any user exceeds 3000 items per table, we need to change this
  const _limit = size <= 0 ? GLOBAL_API_LIMIT : size - 1;
  const from = _page ? _page * size : 0;
  const to = _page ? from + _limit : _limit;

  return { from, to };
};

export interface SortOptions<T = string> {
  sortBy: T;
  sortDirection: "asc" | "desc";
}

export const transformJoinKeys = (searchableItem: string | number | symbol) => {
  const fullKey = String(searchableItem);
  const isEmbeddedResource = fullKey.includes(".");
  const [baseKey, attributeKey] = fullKey.split(".");
  return {
    fullKey,
    baseKey,
    isEmbeddedResource,
    attributeKey: attributeKey ? attributeKey : undefined,
  };
};

export const getOrder = (
  sortBy: SortOptions["sortBy"],
  sortDirection: SortOptions["sortDirection"],
  sortable: (string | number | symbol)[],
) => {
  if (!sortBy || !sortable.includes(sortBy)) {
    return {
      orderBy: undefined,
      orderAsc: true,
    };
  }

  const { baseKey, attributeKey } = transformJoinKeys(sortBy);

  // see https://supabase.com/docs/reference/javascript/order
  const orderBy = attributeKey ? `${baseKey}(${attributeKey})` : baseKey;
  const orderAsc = sortDirection === "asc";
  return {
    orderBy,
    orderAsc,
  };
};

interface SearchILikeItem {
  key: string;
  value: string;
}
export const getSearch = (search: string, searchables: (string | number | symbol)[]) => {
  // ! CURRENT LIMITATION (Jan 2025): search works on nested resources, but only one level deep
  // ! (eg. on device -> manufacturer.name, but NOT on case -> device -> manufacturer.name)
  // ! WORKING EXAMPLE FOR DEVICES
  // ! ilike -> search on embedded resources fields
  // ! .or -> search on base entity fields
  // ! .or -> embedded resources base entity not.is.nulls to allow filtering
  // .ilike("manufacturer.name", `"%${searchTerm}%""`)
  // .ilike("model.name", `"%${searchTerm}%""`)
  // .or(
  //   `name.ilike."%${searchTerm}%"",ref.ilike."%${searchTerm}%"",software_version.ilike."%${searchTerm}%"",manufacturer.not.is.null, model.not.is.null`,
  // );

  const amendedSearchTerm = `%${search}%`;

  const iLike: SearchILikeItem[] = [];
  // using Sets to deduplicate entries, which breaks postgrest
  const baseEntitySearch = new Set<string>();
  const embeddedResourceFilters = new Set<string>();

  for (const searchable of searchables) {
    const { isEmbeddedResource, fullKey, baseKey } = transformJoinKeys(searchable);
    if (isEmbeddedResource) {
      iLike.push({ key: fullKey, value: amendedSearchTerm });
      embeddedResourceFilters.add(`${baseKey}.not.is.null`);
    } else {
      baseEntitySearch.add(`${baseKey}.ilike."${amendedSearchTerm}"`);
    }
  }

  return {
    iLike,
    or: [...Array.from(baseEntitySearch), ...Array.from(embeddedResourceFilters)].join(","),
  };
};

export const defaultSortByDeviceNameAsc: SortOptions = { sortBy: "device.name", sortDirection: "asc" }; // devices
export const defaultSortByNameAsc: SortOptions = { sortBy: "name", sortDirection: "asc" }; // devices
export const defaultSortByFirstNameAsc: SortOptions = { sortBy: "first_name", sortDirection: "asc" }; // users
export const defaultSortByTitleAsc: SortOptions = { sortBy: "title", sortDirection: "asc" }; // cases open, maintenance strategies
export const defaultSortByFinishedAtDesc: SortOptions = { sortBy: "finished_at", sortDirection: "desc" }; // maintenances / cases done
export const defaultSortByStartedAtDesc: SortOptions = { sortBy: "started_at", sortDirection: "desc" }; // maintenances / cases done
export const defaultSortByStartAtDesc: SortOptions = { sortBy: "start_at", sortDirection: "desc" }; // service jobs
export const defaultSortByStartAtAsc: SortOptions = { sortBy: "start_at", sortDirection: "asc" }; // service jobs
export const defaultSortByResolvedAtDesc: SortOptions = { sortBy: "resolved_at", sortDirection: "desc" }; // maintenances / cases done
export const defaultSortByPlannedDateDesc: SortOptions = { sortBy: "planned_date", sortDirection: "desc" }; // maintenances open
export const defaultSortByPlannedDateAsc: SortOptions = { sortBy: "planned_date", sortDirection: "asc" }; // maintenances upcoming
export const defaultSortByUpdatedAtDesc: SortOptions = { sortBy: "updated_at", sortDirection: "desc" }; // cases open
export const defaultSortByCreatedAtAsc: SortOptions = { sortBy: "created_at", sortDirection: "asc" }; // part locations (there is no real sensible way to sort them otherwise)
export const defaultSortByCreatedAtDesc: SortOptions = { sortBy: "created_at", sortDirection: "desc" }; // cases by device
export const defaultSortByRelevantDateDesc: SortOptions = { sortBy: "relevant_date", sortDirection: "desc" }; // device logs
export const defaultSortByExecutedAtDesc: SortOptions = { sortBy: "executed_at", sortDirection: "desc" }; // parts
export const defaultSortByReportedAtDesc: SortOptions = { sortBy: "reported_at", sortDirection: "desc" }; // cases
export const defaultSortByAssessedAtDesc: SortOptions = { sortBy: "assessed_at", sortDirection: "desc" }; // cases
